import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import enTranslation from './locales/en/translation.json';

export const supportedLanguages = ['en', 'es', 'ru', 'pt'];

// Dynamic loaders for non-EN languages
const translationLoaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  es: () => import('./locales/es/translation.json'),
  ru: () => import('./locales/ru/translation.json'),
  pt: () => import('./locales/pt/translation.json'),
};

export async function loadTranslation(lang: string): Promise<void> {
  if (lang === 'en' || !translationLoaders[lang]) return;
  if (i18n.hasResourceBundle(lang, 'translation')) return; // already loaded
  const module = await translationLoaders[lang]();
  i18n.addResourceBundle(lang, 'translation', module.default, true, true);
}

// Detect language with priority: URL param > localStorage > browser > default
const getInitialLanguage = (): string => {
  if (typeof window === 'undefined') return 'en';

  try {
    // 1. Check URL parameter (highest priority)
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('lang');
    if (urlLang && supportedLanguages.includes(urlLang)) {
      // Store URL language preference in localStorage
      localStorage.setItem('language', urlLang);
      return urlLang;
    }

    // 2. Check localStorage (second priority)
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage && supportedLanguages.includes(savedLanguage)) {
      return savedLanguage;
    }

    // 3. Detect from browser (third priority)
    const browserLang = navigator.language.split('-')[0];
    if (supportedLanguages.includes(browserLang)) {
      return browserLang;
    }
  } catch (error) {
    console.warn('Error detecting language:', error);
  }

  // 4. Default fallback
  return 'en';
};

const initialLang = getInitialLanguage();

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: enTranslation },
  },
  lng: initialLang,
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: false, // Disable suspense to avoid loading issues
    // Re-render translation consumers when a resource bundle is added at runtime.
    // loadTranslation() calls addResourceBundle() after a dynamic import; without this,
    // a non-EN first load renders the EN fallback forever (default bindI18nStore is '').
    // Note: 'added' re-renders all mounted consumers on ANY bundle add (not just the
    // active language) — fine today since only the active language is ever loaded.
    bindI18nStore: 'added',
  },
  debug: false, // Set to true to see i18n debug logs
});

// Load initial language if not EN
if (initialLang !== 'en') {
  loadTranslation(initialLang);
}

// Load translations on language change
i18n.on('languageChanged', (lang: string) => {
  loadTranslation(lang);
});

export default i18n;
